import React, { useState, useEffect, useCallback, useRef } from 'react';
import './Services.css';

/** High-level groupings for the service tab bar — four groups aligned with Home service cards. */
const SERVICE_MACRO_GROUPS = {
  concrete: {
    label: 'Concrete testing',
    shortLabel: 'Concrete',
    categories: ['Concrete']
  },
  soilGround: {
    label: 'Soil testing & ground improvement',
    shortLabel: 'Soil / ground',
    categories: [
      'Soil & Soil Aggregates Testing',
      'Coarse and Fine Aggregates',
      'In-Situ Testing',
      'Geotechnical Investigation',
      'Surveying Services'
    ]
  },
  steelPile: {
    label: 'Steel & pile testing',
    shortLabel: 'Steel / pile',
    categories: ['Metal', 'Pile Testing']
  },
  ndt: {
    label: 'Non-destructive testing',
    shortLabel: 'Non-destructive',
    categories: ['Non-destructive Testing']
  }
};

function getMacroGroupForCategory(tab) {
  const entry = Object.entries(SERVICE_MACRO_GROUPS).find(([, g]) => g.categories.includes(tab));
  return entry ? entry[0] : 'concrete';
}

const TEST_IMAGE_PLACEHOLDER = `${process.env.PUBLIC_URL || ''}/Testing/test-placeholder.svg`;

function onTestImageError(e) {
  const el = e.currentTarget;
  if (el.src.includes('test-placeholder.svg')) return;
  el.onerror = null;
  el.src = TEST_IMAGE_PLACEHOLDER;
}

/** Hero intro carousel: prefers `service.images` (curated hero set). If absent/empty, falls back to sorted unique `groupImages` (Tests & methods thumbnails). */
function buildCarouselImageList(service) {
  if (!service) return [];
  const hero = Array.isArray(service.images)
    ? service.images.filter((u) => typeof u === 'string' && u.length > 0)
    : [];
  if (hero.length > 0) return hero;
  const groups = service.groupImages;
  if (groups && typeof groups === 'object' && Object.keys(groups).length > 0) {
    const keys = Object.keys(groups).sort((a, b) => String(a).localeCompare(String(b)));
    const seen = new Set();
    const out = [];
    keys.forEach((k) => {
      const url = groups[k];
      if (typeof url === 'string' && url.length > 0 && !seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    });
    return out;
  }
  return [];
}

const Services = ({ setCurrentPage, setActiveSection, targetServiceTab, onTargetServiceTabApplied }) => {
  const [activeTab, setActiveTab] = useState('Concrete');
  const [displayedTab, setDisplayedTab] = useState('Concrete');
  const [contentAnimPhase, setContentAnimPhase] = useState('enter'); // 'enter' | 'exit' | 'idle'
  const transitionTimeoutRef = useRef(null);
  const [serviceMacroGroup, setServiceMacroGroup] = useState('concrete');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedImages, setLoadedImages] = useState(new Set());
  const [loadingImages, setLoadingImages] = useState(new Set());
  const loadedImagesRef = useRef(loadedImages);

  useEffect(() => {
    loadedImagesRef.current = loadedImages;
  }, [loadedImages]);

  const testsListRef = useRef(null);
  /** Expanded rows for sub-tests that have extended definitions (itemDescriptions). */
  const [methodDrawerKeys, setMethodDrawerKeys] = useState(() => new Set());
  /** Tests & methods card: fullscreen photo preview (same UX as branch map cards). */
  const [serviceTestPhotoLightbox, setServiceTestPhotoLightbox] = useState(null);

  const services = {
    'Concrete': {
      title: 'Concrete Testing',
      description: 'Comprehensive concrete testing services ensuring quality assurance and compliance with industry standards. Our state-of-the-art facilities and expert team deliver accurate, reliable results for all your concrete testing needs.',
      groupDescriptions: {
        'Compressive Test of Concrete Cylinder': 'This test method determines compressive strength of cylindrical concrete specimen such as molded and drilled cores with a value either SI or inch-pound units. The result of this test method is used as a basis for quality control of concrete proportioning, mixing, and placing operations, determination of compliance with specifications, control for evaluating effectiveness of admixtures, and similar uses.',
        'Flexural Test of Concrete': 'Determines the bending (flexural) strength of concrete—commonly used for evaluating beams and slabs.',
        'Compressive Test of Mortar Cube': 'Measures the compressive strength of mortar cubes to verify the quality of the mix design.',
        'Compressive Test of CHB': 'Tests the compressive strength of hollow blocks (CHB) to ensure structural performance and compliance.',
        'Core Extraction': 'Extracts core samples from existing concrete for laboratory testing (e.g., strength verification and other condition checks).'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Compressive Test of Concrete Cylinder':
            '/Testing/Compressive test of concrete cylinder/website-cover.jpg',
          'Flexural Test of Concrete': '/Testing/flexural test of beam/website-cover.jpg',
          'Compressive Test of Mortar Cube':
            '/Testing/compressive test of mortar cubes/website-cover.jpg',
          'Compressive Test of CHB': '/Testing/compressive test of chb/website-cover.jpg',
          'Core Extraction': '/Testing/Core Extraction/website-cover.png'
        }).map(([k, p]) => [k, process.env.PUBLIC_URL + p])
      ),
      images: [
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/COMPRESSIVE TEST OF CONCRETE CYLINDER/comp.jpg',
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/REBOUND HAMMER TESTING/RH1.jpg',
        '/Testing/Coring/448746160_849501266700021_5495570982047354487_n.jpg',
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/CORE EXTRACTION/379592894_210722842020470_4479544368863620171_n.jpg',
        '/Testing/Structural Investigation/382130678_332544565800603_9142492464602758453_n.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Compressive Test of Concrete Cylinder',
        'Flexural Test of Concrete',
        'Compressive Test of Mortar Cube',
        'Compressive Test of CHB',
        'Core Extraction'
      ]
    },
    'Metal': {
      title: 'Metal Testing',
      description: 'Comprehensive metal testing services including reinforcing steel bars, anchor bolts, M.S. plates, mechanical splices, and wire strands with physical, mechanical, and chemical analysis.',
      groupDescriptions: {
        'Reinforcing Steel Bar': 'Evaluates reinforcing bars using physical, mechanical, and chemical tests (e.g., tensile, bend, elongation, and other properties).',
        'Anchor Bolts': 'Verifies the mechanical capacity of anchor bolts using tensile, bend, and elongation tests.',
        'M.S. Plates': 'Checks mechanical and chemical properties of M.S. plates to confirm material quality.',
        'Mechanical Splice': 'Assesses the performance of mechanical splices (e.g., tensile and slip tensile tests) to ensure connection reliability.',
        'Wire Strand': 'Tests wire strands for tensile strength, elongation, and overall strand quality.'
      },
      itemDescriptions: {
        '• Physical Test': 'Physical tests verify basic properties of the steel such as dimensional/shape conformity and other measurable characteristics.',
        '• Mechanical Test': 'Mechanical tests determine how the steel behaves under load (strength and ductility).',
        'Tensile Test': 'Measures tensile strength and related performance of the steel under tension.',
        'Bend Test': 'Evaluates ductility and bending behavior of the steel by bending it to check cracking or unacceptable deformation.',
        'Elongation': 'Determines how much the steel can stretch before failure (ductility indicator).',
        '• Chemical Test': 'Chemical testing checks the chemical composition of the steel to confirm required material grade and properties.',
        '• Zinc Coating Thickness': 'Measures the thickness of zinc coating to verify corrosion protection requirements.',
        '• Tensile Test': 'Measures tensile strength of the component under tension.',
        '• Slip Tensile Test': 'Evaluates slip tensile behavior to confirm performance of mechanical splices under load.',
        '• Elongation': 'Measures elongation/ductility during mechanical evaluation.'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Anchor Bolts': '/Testing/Anchor Bolts/website-cover.png',
          'Mechanical Splice': '/Testing/Mechanical Splice/website-cover.png',
          'M.S. Plates': '/Testing/M.S. Plates/website-cover.png',
          'Reinforcing Steel Bar': '/Testing/Tensile Strength Test/website-cover.jpg',
          'Wire Strand': '/Testing/Bending Test/website-cover.jpg'
        }).map(([k, p]) => [k, process.env.PUBLIC_URL + p])
      ),
      images: [
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/REBAR TENSILE AND BENDING TEST/1.jpg',
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/REBAR SCANNING/RS1.jpg',
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/DYE PENETRANT TESTING/DP1.jpg',
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/REBOUND HAMMER TESTING/RH1.jpg',
        '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/ULTRASONIC PULSE VELOCITY TEST/UP1.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Reinforcing Steel Bar',
        '  • Physical Test',
        '  • Mechanical Test',
        '     Tensile Test',
        '     Bend Test', 
        '     Elongation',
        '  • Chemical Test',
        'Anchor Bolts',
        '  • Mechanical Test',
        '     Tensile Test',
        '     Bend Test',
        '     Elongation',
        '  • Zinc Coating Thickness',
        'M.S. Plates',
        '  • Mechanical Test',
        '     Tensile Test',
        '     Bend Test',
        '     Elongation',
        '  • Chemical Test',
        'Mechanical Splice',
        '  • Tensile Test',
        '  • Slip Tensile Test',
        'Wire Strand',
        '  • Tensile Test',
        '  • Elongation'
      ]
    },
    'Pile Testing': {
      title: 'Pile Testing',
      description: 'Comprehensive pile testing services to verify foundation integrity and load-bearing capacity using advanced testing methods.',
      groupDescriptions: {
        'Low Strain - Pile Integrity Test (PIT)': 'Identifies pile integrity and possible defects using a low-strain method to check for damage or discontinuities.',
        'High Strain - Pile Dynamic Analysis (PDA)': 'Performs high-strain dynamic testing to evaluate pile behavior and load-bearing response.',
        'Cross hole sonic logging test (CSL)': 'Uses non-destructive ultrasonic testing between access tubes to assess pile quality and integrity.'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Cross hole sonic logging test (CSL)':
            '/Testing/Cross-hole Sonic Logging (CSL)/website-cover.jpg',
          'High Strain - Pile Dynamic Analysis (PDA)':
            '/Testing/Pile Dynamic Analyser/PDA.jpg',
          'Low Strain - Pile Integrity Test (PIT)':
            '/Testing/Pile Integrity Test/PIT.jpg'
        }).map(([k, p]) => [k, process.env.PUBLIC_URL + p])
      ),
      images: [
        '/Testing/Pile Integrity Test/website-cover.PNG',
        '/Testing/Pile Integrity Test/IMG_0209.PNG',
        '/Testing/Cross-hole Sonic Logging (CSL)/1.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Low Strain - Pile Integrity Test (PIT)',
        'High Strain - Pile Dynamic Analysis (PDA)',
        'Cross hole sonic logging test (CSL)'
      ]
    },
    'Soil & Soil Aggregates Testing': {
      title: 'Soil & Soil Aggregates Testing',
      description:
        'Laboratory testing of soil and soil–aggregate materials for construction projects—including grading, Atterberg limits, moisture–density (Proctor), CBR, specific gravity, consolidation, direct shear, and triaxial strength tests—for design checks and quality assurance.',
      groupDescriptions: {
        'Quality Test of Soil': 'Verifies key soil properties (e.g., grading, Atterberg limits, moisture content, CBR, specific gravity) for proper foundation design.',
        'Direct Shear Test': 'Measures soil shear strength parameters to determine resistance to sliding and related bearing capacity behavior.',
        'Consolidation Test': 'Evaluates settlement and compressibility characteristics of soil under applied loads.',
        'Triaxial Test': 'Provides detailed strength parameters for assessing soil stability and strength for the project.'
      },
      itemDescriptions: {
        '• Grading / Sieve Analysis': 'The sieve analysis is a method employed to assess the gradation, which pertains to the distribution of aggregate particles based on size within a specified sample. This analysis is crucial for verifying compliance with design standards, adhering to production control criteria, and meeting verification specifications. The data obtained from gradation analysis serves various purposes, including calculating correlations among different aggregates or aggregate blends, ensuring adherence to specified blends, and forecasting production trends through the graphical representation of gradation curves. These applications highlight the versatility and importance of the gradation data derived from sieve analysis in the realm of aggregate testing.',
        '• Atterberg Limit': 'These testing methods are essential for characterizing the fine-grained soil components and are used in many engineering categorization systems. They are also used to define the fine-grained component of building supplies. Liquid limit, plastic limit, and plasticity index of soils are commonly used, separately or in conjunction with other properties of the soil, to establish relationships with engineering behaviors like compressibility, shrink-swell, compatibility, hydraulic conductivity (permeability), and shear strength. These techniques are occasionally applied to evaluate the weathering properties of clay-shale materials. When these materials are subjected to frequent cycles of wetting and drying, their liquid limits generally increase. The degree of this rise is thought to indicate how weatherable a shale is.',
        '• Soil Classification': 'Classifies soil type based on measured properties to support selection of design parameters.',
        '• Moisture Content Determination': 'Determines the water content of soil, used to control compaction and interpret strength behavior.',
        '• Moisture Density Relation (MDR)': 'The Standard Proctor test (also known as Moisture Density Relation test) determines the relationship between molding water content and dry unit of soils (compaction curve) compacted in a 4 or 6-in (101.6 or 152.4-mm) diameter mold with a 5.50 lbf rammer dropped from a height of 12.0 in, producing a compactive effort of 600 kN-m/m³.',
        '• California Bearing Ratio (CBR)': 'The California Bearing Ratio (CBR) is a test method that assesses the strength of compacted soil specimens with a maximum particle size of less than 3 Ú 4 in. (19 mm). It allows for material gradation modifications to ensure all specimens pass the total gravel fraction. However, modified materials may have different strength properties. CBR results for materials with substantial particles retained on the No. 4 sieve are more variable than finer materials, and more trials may be required for a reliable CBR. Clients can specify water content or dry unit weight, and specimens must be soaked before penetration. The CBR is industry standards, and the user is responsible for establishing safety, health, and environmental practices.',
        '• Specific Gravity of Soil': 'Measures the specific gravity of soil solids to support calculations for void ratio and unit weight.',
        '• Consolidated Undrained': 'Triaxial test mode that simulates loading with drainage conditions representative of field behavior.',
        '• Unconsolidated Undrained': 'Triaxial test mode for short-term loading behavior without full consolidation prior to shearing.',
        '• Consolidated Drained': 'Triaxial test mode where consolidation is allowed and drainage occurs during shearing.'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Consolidation Test': '/Testing/Consolidation Test/website-cover.jpg',
          'Direct Shear Test': '/Testing/Direct Shear Test/website-cover.jpg',
          'Quality Test of Soil': '/Testing/Quality Test of Soil/website-cover.png',
          'Triaxial Test': '/Testing/Triaxial Test/website-cover.png'
        }).map(([k, p]) => [k, process.env.PUBLIC_URL + p])
      ),
      images: [
        '/Testing/Quality Test of Soil/website-cover.png',
        '/Testing/Direct Shear Test/website-cover.jpg',
        '/Testing/Consolidation Test/website-cover.jpg',
        '/Testing/Triaxial Test/website-cover.png',
        '/Testing/Quality Test of Soil/Quality Test of soil.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Quality Test of Soil',
        '  • Grading / Sieve Analysis',
        '  • Atterberg Limit',
        '  • Soil Classification',
        '  • Moisture Content Determination',
        '  • Moisture Density Relation (MDR)',
        '  • California Bearing Ratio (CBR)',
        '  • Specific Gravity of Soil',
        'Direct Shear Test',
        'Consolidation Test',
        'Triaxial Test',
        '  • Consolidated Undrained',
        '  • Unconsolidated Undrained',
        '  • Consolidated Drained'
      ]
    },
    'Coarse and Fine Aggregates': {
      title: 'Coarse and Fine Aggregates Testing',
      description: 'Comprehensive testing of coarse and fine aggregates to ensure quality and compliance with construction standards.',
      groupDescriptions: {
        'Quality Test of Coarse Aggregates': 'Tests the quality of coarse aggregates for mix performance (e.g., grading, soundness, Los Angeles abrasion, specific gravity, and unit weight).',
        'Quality Test of Fine Aggregates': 'Tests the quality of fine aggregates/sand for mix suitability (e.g., grading, clay lumps, sand equivalent, organic impurities, and fineness modulus).',
        'Aggregate Crushing Value': 'Measures the resistance of aggregates to crushing to determine their contribution to concrete strength.',
        'Alkali Silica Reactivity': 'Checks the potential for aggregates to trigger ASR (alkali-silica reaction) to help prevent premature deterioration.'
      },
      itemDescriptions: {
        '• Grading / Sieve Analysis': 'The sieve analysis is a method employed to assess the gradation, which pertains to the distribution of aggregate particles based on size within a specified sample. This analysis is crucial for verifying compliance with design standards, adhering to production control criteria, and meeting verification specifications. The data obtained from gradation analysis serves various purposes, including calculating correlations among different aggregates or aggregate blends, ensuring adherence to specified blends, and forecasting production trends through the graphical representation of gradation curves. These applications highlight the versatility and importance of the gradation data derived from sieve analysis in the realm of aggregate testing.',
        '• Clay lumps': 'Checks the presence of clay lumps that can weaken bond and reduce concrete performance.',
        '• Soundness': 'Evaluates aggregate durability by checking resistance to breakdown and weathering effects.',
        '• Los Angeles Abrasion': 'Measures abrasion resistance to estimate aggregate toughness for concrete durability.',
        '• Specific Gravity (Coarse)': 'Distinction is made between the bulk density of aggregates, which takes into account the volume of voids between aggregate particles, and the density of aggregate particles, as measured by Test Method C29/C29M. Also, ASTM C127 aims to find the absorption of coarse aggregate, where absorption is the process by which water is drawn into and tends to fill the permeable pores in a porous solid body.',
        '• Specific Gravity (Fine)': 'Distinction is made between the bulk density of aggregates, which takes into account the volume of voids between aggregate particles, and the density of aggregate particles, as measured by Test Method C29/C29M. Also, ASTM C128 aims to find the absorption of fine aggregate, where absorption is the process by which water is drawn into and tends to fill the permeable pores in a porous solid body.',
        '• Unit Weight': 'Measures bulk/unit weight to help verify aggregate density and mix proportions.',
        '• Fractured Face': 'Checks the percentage of fractured/rough faces affecting interlock and strength.',
        '• Material Finer than Sieve #200': 'Measures fine content to reduce risks of excessive fines affecting strength and workability.',
        '• Sand Equivalent': 'Assesses the proportion of clay/dust contamination in sand to support mix quality.',
        '• Organic Impurities': 'Detects organic materials that may delay set and affect concrete strength.',
        '• Fineness modulus of sand': 'Summarizes sand fineness to support proportioning for concrete mixes.'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Aggregate Crushing Value': '/Testing/Aggregate Crushing Value/website-cover.png',
          'Alkali Silica Reactivity': '/Testing/Chemical Composition test/website-cover.jpg',
          'Quality Test of Coarse Aggregates':
            '/Testing/quality test of aggregates/website-cover.jpg',
          'Quality Test of Fine Aggregates':
            '/Testing/Quality Test of Fine Aggregates/website-cover.png'
        }).map(([k, v]) => [k, process.env.PUBLIC_URL + v])
      ),
      images: [
        '/Testing/quality test of aggregates/website-cover.jpg',
        '/Testing/Field Density Test ( Sandcone)/website-cover.jpg',
        '/Testing/Chemical Composition test/website-cover.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Quality Test of Coarse Aggregates',
        '  • Grading / Sieve Analysis',
        '  • Clay lumps',
        '  • Soundness',
        '  • Los Angeles Abrasion',
        '  • Specific Gravity (Coarse)',
        '  • Unit Weight',
        '  • Fractured Face',
        '  • Material Finer than Sieve #200',
        'Quality Test of Fine Aggregates',
        '  • Grading / Sieve Analysis',
        '  • Material Finer than Sieve #200',
        '  • Clay lumps',
        '  • Soundness',
        '  • Specific Gravity (Fine)',
        '  • Unit Weight',
        '  • Sand Equivalent',
        '  • Organic Impurities',
        '  • Fineness modulus of sand',
        'Aggregate Crushing Value',
        'Alkali Silica Reactivity'
      ]
    },
    'In-Situ Testing': {
      title: 'In-Situ Testing',
      description: 'Field testing services to evaluate soil properties and construction quality at the project site.',
      groupDescriptions: {
        'Field Density Test': 'Measures the in-place density/compaction of soil on site using methods such as the sand cone test or electric density gauge to confirm proper backfill/compaction.',
        'Dynamic Cone Penetration Test': 'This test procedure is used to evaluate the in situ strength of intact soil and highly compacted materials, or both. The 8-kg (17.6-lb) DCP\'s penetration rate can be utilized to estimate the in-situ CBR (California Bearing Ratio), determine the thickness of strata, assess the shear strength of strata, and discover other material features.',
        'Plate Load Test': 'The values are expressed in SI units or inch pound units and should be used independently. Combining values from both systems may result in nonconformance with the standard. The standard does not address all safety concerns associated with its use, and users must establish appropriate safety and health practices and determine regulatory limitations before use.',
        'Static Load Test': 'Static Load Testing is the measurement of load and deflection at the pile top. The test is designed to mimic the actual \u2018worst case\u2019 load stresses the pile will experience when integrated into the structure it has been designed to support.',
        'Pull-off Test': 'This test method covers a procedure for evaluating the pull-off strength of a coating system from metal substrates which is commonly referred to adhesion. Pull-off adhesion test works by applying perpendicular tensile force that measures the resistance of coating to its substrate. This test can be destructive and spot repairs may be necessary.',
        'Pull-Out Test': 'These tests are conducted under authority requirements and are intended for anchorage devices installed perpendicular to a structural member\'s plane surface. However, combined tension and shear tests and torsion tests are not covered. The document does not address all safety concerns and users must establish appropriate safety and health practices before use. The test methods are essential for obtaining design data and specifications for anchorage devices in structural members.',
        'Crack Determination Test': 'Detects and assesses cracks to evaluate the condition and possible construction-related causes.',
        'Rebar Scanning': 'Identifies reinforcement bar location and cover depth using scanning for construction verification and drilling control.',
        'Ground Resistivity Test (Grounding)': 'Measures soil electrical resistivity to support proper grounding system design.',
        'Ground Resistivity Test (Water Detector)': 'Helps detect/assess the influence of groundwater using resistivity measurements.',
        'Thermal Resistivity Test': 'Measures soil thermal resistivity for thermal design of ground-based systems.'
      },
      itemDescriptions: {
        '• Using Sand Cone Method': 'The Standard Test Method for Density and Unit Weight of Soil In-Place by Sand-Cone Method (ASTM D1556), issued in July 2023. This provides a comprehensive manual outlining resources, safety precautions, and methodical steps for accurate test results. The objective of this paper is to ensure the uniformity and clarity in test execution, promoting a secure and productive work environment.',
        '• Using Electric Density Gauge': 'The Electrical Density Gauge (EDG) is a non-nuclear way to find out how wet and dense the compacted soils used for foundations and roadbeds. The EDG is a battery-powered, movable tool that can be used anywhere without the worries and rules that come with nuclear safety.'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Crack Determination Test':
            '/Testing/Crack Length & Depth Testing/website-cover.jpg',
          'Dynamic Cone Penetration Test':
            '/Testing/Dynamic Cone Penetration (DCP)/website-cover.jpg',
          'Field Density Test': '/Testing/Field Density Test ( EDG)/website-cover.jpg',
          'Ground Resistivity Test (Grounding)':
            '/Testing/Geo Resistivity Earth Grounding/website-cover.PNG',
          'Ground Resistivity Test (Water Detector)':
            '/Testing/Underground Water Detector/website-cover.PNG',
          'Plate Load Test': '/Testing/Plateload Test/website-cover.jpg',
          'Static Load Test': '/Testing/Static Load Test/website-cover.png',
          'Pull-off Test': '/Testing/Pull-off Test/website-cover.jpg',
          'Pull-Out Test': '/Testing/Pull-out Test/website-cover.jpg',
          'Rebar Scanning':
            '/Testing/PHOTOS (DIFFERENT TESTING) for WEBSITE/REBAR SCANNING/website-cover.jpg',
          'Thermal Resistivity Test': '/Testing/Thermal Resistivity/website-cover.jpg'
        }).map(([k, v]) => [k, process.env.PUBLIC_URL + v])
      ),
      images: [
        '/Testing/Field Density Test ( EDG)/website-cover.jpg',
        '/Testing/Field Density Test ( Sandcone)/website-cover.jpg',
        '/Testing/Dynamic Cone Penetration (DCP)/website-cover.jpg',
        '/Testing/Plateload Test/website-cover.jpg',
        '/Testing/Static Load Test/website-cover.png',
        '/Testing/Pull-off Test/website-cover.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Field Density Test',
        '  • Using Sand Cone Method',
        '  • Using Electric Density Gauge',
        'Dynamic Cone Penetration Test',
        'Plate Load Test',
        'Static Load Test',
        'Pull-off Test',
        'Pull-Out Test',
        'Crack Determination Test',
        'Rebar Scanning',
        'Ground Resistivity Test (Grounding)',
        'Ground Resistivity Test (Water Detector)',
        'Thermal Resistivity Test'
      ]
    },
    'Non-destructive Testing': {
      title: 'Non-destructive Testing (NDT)',
      description: 'Advanced non-destructive testing methods to evaluate material properties and structural integrity without causing damage.',
      groupDescriptions: {
        'Ultrasonic pulse velocity': 'Assesses concrete quality and uniformity using ultrasonic pulse velocity to identify potential defects and homogeneity issues.',
        'Crack Depth Measurement': 'This test method covers the determination of the propagation velocity of longitudinal stress wave pulses through concrete. This test method does not apply to the propagation of other types of stress waves through concrete.',
        'Rebound hammer test': 'Estimates relative concrete strength by measuring surface hardness (rebound values).',
        'Ultrasonic weld test (UT)': 'Uses ultrasonic methods to check weld integrity and detect possible discontinuities.',
        'Magnetic particle test (MT/MPI)': 'Detects near-surface cracks in ferromagnetic materials using magnetic fields and visible indications.',
        'Dye penetrant test (PT)': 'Penetrant testing is a nondestructive test technique to find discontinuities that are visible at the surface such as laminations, shrinkage, cold closures, laps, seams, and cracks via leaks or a deficiency in fusion, and it pertains to in-process, final and upkeep inspections. These processes can be used for the identification of discontinuities that are open or attached to the component\'s surface that is being examined.',
        'Dry Film Thickness Test (DFT)': 'Measures the thickness of protective coatings/paint (dry film) for compliance and performance verification.'
      },
      images: [
        '/Testing/Rebound Hammer Test/website-cover.jpg',
        '/Testing/Penetrant Test (DPT)/website-cover.jpg',
        '/Testing/Ultrasonic Pulse velocity test/UPV.png',
        '/Testing/Utrasonic Flaw Detector/website-cover.png',
        '/Testing/Magnetic Particles  Testing/website-cover.jpg',
        '/Testing/Dry-Film Thickness Gauge/website-cover.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      /** Per-method thumbnails (sorted headers). Fallback is images[groupIndex % length] if omitted. */
      groupImages: Object.fromEntries(
        Object.entries({
          'Crack Depth Measurement':
            '/Testing/Crack Depth Measurement/crack-depth.jpg',
          'Dry Film Thickness Test (DFT)':
            '/Testing/Dry-Film Thickness Gauge/website-cover.jpg',
          'Dye penetrant test (PT)': '/Testing/Penetrant Test (DPT)/website-cover.jpg',
          'Magnetic particle test (MT/MPI)':
            '/Testing/Magnetic Particles  Testing/website-cover.jpg',
          'Rebound hammer test': '/Testing/Rebound Hammer Test/Rebound Hammer.jpg',
          'Ultrasonic pulse velocity':
            '/Testing/Ultrasonic Pulse velocity test/UPV.png',
          'Ultrasonic weld test (UT)': '/Testing/Ultrasonic Weld Test/UT.jpg'
        }).map(([k, p]) => [k, process.env.PUBLIC_URL + p])
      ),
      items: [
        'Ultrasonic pulse velocity',
        'Crack Depth Measurement',
        'Rebound hammer test',
        'Ultrasonic weld test (UT)',
        'Magnetic particle test (MT/MPI)',
        'Dye penetrant test (PT)',
        'Dry Film Thickness Test (DFT)'
      ]
    },
    'Geotechnical Investigation': {
      title: 'Geotechnical Investigation',
      description: 'Comprehensive geotechnical investigation services including soil investigation, micro piling, and ground improvement techniques.',
      groupDescriptions: {
        'Soil Investigation': 'This test method uses disturbed soil samples for moisture content determination, identification, and classification. It\'s suitable for soil from samplers producing large shear strain disturbances. However, sample quality isn\'t suitable for advanced laboratory testing for engineering properties. Thin wall tube samplers and coring techniques may reduce disturbance in soft and harder soils. This method is used in geotechnical exploration and evaluates liquefaction potential during earthquakes.',
        'UCS': 'Rock core samples are heated, flattened, and subjected to a battery of tests until failure and peak load are achieved. The unconfined compressive strength of the rock is frequently used in design formulas; however, laboratory measurements are not a reliable indicator of large-scale in situ properties due to joints, faults, and other features. Laboratory values for intact specimens must thus be used with caution in engineering applications.',
        'Micro piling': 'Reinforces foundations using micro piles to improve structural stability in challenging ground conditions.',
        'Ground Improvement': 'Ground improvement techniques (e.g., jet grouting and pressure grouting) used to make the ground safer and more stable.'
      },
      itemDescriptions: {
        '• Jet Grouting': 'A ground improvement method that uses high-pressure grout injection to create improved soil/cement columns.',
        '• Soil Cement Design Mix': 'A Soil-Cement Design Mixture was performed to be use a basis for a ground improvement method of a shallow foundation which aims to meet strength requirements of different applications (based on project specifications).',
        '• Pressure Grouting': 'Injects grout under pressure to fill voids and strengthen the soil mass for improved stability.'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Ground Improvement': '/Testing/Jet Grouting/website-cover.jpg',
          'Micro piling': '/Testing/Micropilling/website-cover.jpg',
          'Soil Investigation':
            '/Testing/SOIL INVESTIGATION PROJECTS-20250717T013447Z-1-001/SOIL INVESTIGATION PROJECTS/website-cover.jpg',
          UCS: '/Testing/UCS/website-cover.png'
        }).map(([k, v]) => [k, process.env.PUBLIC_URL + v])
      ),
      images: [
        '/Testing/SOIL INVESTIGATION PROJECTS-20250717T013447Z-1-001/SOIL INVESTIGATION PROJECTS/website-cover.jpg',
        '/Testing/Jet Grouting/website-cover.jpg',
        '/Testing/Micropilling/website-cover.jpg',
        '/Testing/Coring/website-cover.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Soil Investigation',
        'UCS',
        'Micro piling',
        'Ground Improvement',
        '  • Jet Grouting',
        '  • Soil Cement Design Mix',
        '  • Pressure Grouting'
      ]
    },
    'Surveying Services': {
      title: 'Surveying Services',
      description: 'Professional surveying services for construction and development projects including relocation and topographic surveys.',
      groupDescriptions: {
        'Relocation Survey': 'Verifies and/or re-establishes alignments and reference points on site to ensure correct implementation of structures.',
        'Topographic Survey': 'Creates contour/elevation maps and records site features to guide design and construction.'
      },
      groupImages: Object.fromEntries(
        Object.entries({
          'Relocation Survey': '/Testing/Condition-Topographic Survey/website-cover.jpg',
          'Topographic Survey': '/Testing/Topographic Survey/website-cover.png'
        }).map(([k, v]) => [k, process.env.PUBLIC_URL + v])
      ),
      images: [
        '/Testing/Topographic Survey/website-cover.png',
        '/Testing/Condition-Topographic Survey/website-cover.jpg'
      ].map(path => process.env.PUBLIC_URL + path),
      items: [
        'Relocation Survey',
        'Topographic Survey'
      ]
    }
  };

  const categories = [
    'Concrete',
    'Metal',
    'Pile Testing',
    'Soil & Soil Aggregates Testing',
    'Coarse and Fine Aggregates',
    'In-Situ Testing',
    'Non-destructive Testing',
    'Geotechnical Investigation',
    'Surveying Services'
  ];

  const visibleCategories = SERVICE_MACRO_GROUPS[serviceMacroGroup].categories.filter((c) =>
    categories.includes(c)
  );

  const currentService = services[displayedTab];
  const currentItems = currentService?.items || [];
  const groupDescriptionByHeader = currentService?.groupDescriptions || {};
  const itemDescriptions = currentService?.itemDescriptions || {};
  const carouselImages = buildCarouselImageList(currentService);

  useEffect(() => {
    const n = carouselImages.length;
    if (n === 0) return;
    setCurrentSlide((prev) => (prev >= n ? n - 1 : prev));
  }, [displayedTab, carouselImages.length]);

  // Convert flat `items` array into collapsible groups:
  // - Each main category line (no leading spaces) is the accordion header
  // - Everything after it (until next main category) becomes its children list
  const mainTestingGroups = [];
  let activeGroup = null;

  currentItems.forEach((item) => {
    const isMainCategory = !item.startsWith('  ');
    if (isMainCategory) {
      if (activeGroup) mainTestingGroups.push(activeGroup);
      activeGroup = { header: item.trim(), children: [] };
    } else if (activeGroup) {
      activeGroup.children.push(item);
    }
  });

  if (activeGroup) mainTestingGroups.push(activeGroup);

  // Sort accordion headers alphabetically (A-Z).
  // This only changes visual order; group descriptions/items remain keyed by header/text.
  mainTestingGroups.sort((a, b) => String(a.header).localeCompare(String(b.header)));

  // Sort accordion children alphabetically (A-Z) inside each main header.
  // This affects only ordering; indentation/bullet text stays the same so descriptions still match.
  mainTestingGroups.forEach((group) => {
    group.children.sort((a, b) => String(a ?? '').trim().localeCompare(String(b ?? '').trim()));
  });

  const getItemClassName = (item) => {
    const isMainCategory = !item.startsWith('  ');
    const isSubCategory = item.startsWith('  •');
    const isSubSubCategory = item.startsWith('    -');
    return `service-item ${
      isMainCategory ? 'main-category' : isSubCategory ? 'sub-category' : isSubSubCategory ? 'sub-sub-category' : 'regular-item'
    }`;
  };

  const getItemDisplayText = (item) => String(item ?? '').trim();

  const serviceHasMethodDrawers = mainTestingGroups.some((group) =>
    group.children.some((ch) => Boolean(itemDescriptions[getItemDisplayText(ch)]))
  );

  const goToInquire = useCallback(() => {
    setCurrentPage('home');
    setActiveSection('home');
    window.setTimeout(() => {
      document.getElementById('contact-fab')?.click();
    }, 450);
  }, [setCurrentPage, setActiveSection]);

  const toggleMethodDrawer = useCallback((key) => {
    setMethodDrawerKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  useEffect(() => {
    setMethodDrawerKeys(new Set());
  }, [displayedTab]);

  useEffect(() => {
    if (!serviceTestPhotoLightbox) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setServiceTestPhotoLightbox(null);
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      const currentPaddingRight = parseFloat(window.getComputedStyle(document.body).paddingRight) || 0;
      document.body.style.paddingRight = `${currentPaddingRight + scrollbarWidth}px`;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [serviceTestPhotoLightbox]);

  useEffect(() => {
    // Kick an enter animation whenever the displayed tab swaps
    setContentAnimPhase('enter');
    const t = window.setTimeout(() => setContentAnimPhase('idle'), 280);
    return () => window.clearTimeout(t);
  }, [displayedTab]);

  const beginTabTransition = useCallback(
    (nextTab) => {
      if (!nextTab || nextTab === activeTab) return;
      if (!services[nextTab]) return;

      // Clear any in-flight transition
      if (transitionTimeoutRef.current) {
        window.clearTimeout(transitionTimeoutRef.current);
        transitionTimeoutRef.current = null;
      }

      // Highlight the selected tab immediately, but keep old content until exit finishes
      setActiveTab(nextTab);
      setContentAnimPhase('exit');

      transitionTimeoutRef.current = window.setTimeout(() => {
        setDisplayedTab(nextTab);
        setCurrentSlide(0);
        setContentAnimPhase('enter');
        transitionTimeoutRef.current = null;
      }, 220);
    },
    [activeTab, services]
  );

  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) window.clearTimeout(transitionTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (targetServiceTab == null) return;
    if (services[targetServiceTab]) {
      setServiceMacroGroup(getMacroGroupForCategory(targetServiceTab));
      setActiveTab(targetServiceTab);
      setDisplayedTab(targetServiceTab);
      setCurrentSlide(0);
    }
    onTargetServiceTabApplied?.();
  }, [targetServiceTab, onTargetServiceTabApplied]);

  useEffect(() => {
    const root = testsListRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return undefined;

    const els = root.querySelectorAll('[data-reveal]');
    els.forEach((el) => {
      el.classList.remove('is-inview');
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview');
          }
        });
      },
      { threshold: 0.06, rootMargin: '0px 0px -8% 0px' }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [displayedTab]);

  // Image preloading function
  const preloadImage = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        console.log('Image loaded successfully:', src);
        resolve(src);
      };
      img.onerror = (error) => {
        console.warn('Failed to load image:', src, error);
        reject(error);
      };
      img.src = src;
    });
  }, []);

  // Preload only current, previous, and next images for current service
  useEffect(() => {
    const preloadAroundCurrent = async () => {
      const images = buildCarouselImageList(services[displayedTab]);
      if (!images || images.length === 0) return;

      const indicesToLoad = new Set([
        currentSlide,
        (currentSlide - 1 + images.length) % images.length,
        (currentSlide + 1) % images.length
      ]);

      const targets = Array.from(indicesToLoad)
        .map(i => images[i])
        .filter(src => !loadedImagesRef.current.has(src));

      if (targets.length === 0) return;

      setLoadingImages(prev => new Set([...prev, ...targets]));

      try {
        const loaded = [];
        // Limit concurrency to 2
        const queue = [...targets];
        const workers = Array.from({ length: Math.min(2, queue.length) }, async () => {
          while (queue.length) {
            const src = queue.shift();
            if (!src) break;
            try {
              await preloadImage(src);
              loaded.push(src);
            } catch (err) {
              console.warn('Failed to load individual image:', src, err);
            }
          }
        });
        await Promise.all(workers);
        if (loaded.length > 0) {
          setLoadedImages(prev => new Set([...prev, ...loaded]));
        }
      } finally {
        setLoadingImages(new Set());
      }
    };

    preloadAroundCurrent();
  // NOTE: we intentionally do NOT include `carouselImages` or `loadedImages`
  // in dependencies because `services` is recreated each render.
  // This prevents an infinite re-render loop (Maximum update depth exceeded).
  }, [displayedTab, currentSlide, preloadImage]);

  const nextSlide = () => {
    if (!carouselImages.length) return;
    setCurrentSlide((prev) =>
      prev === carouselImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    if (!carouselImages.length) return;
    setCurrentSlide((prev) =>
      prev === 0 ? carouselImages.length - 1 : prev - 1
    );
  };

  // Simple dots scrolling - show 5 dots at a time
  const getDotsTransform = () => {
    const totalDots = carouselImages.length;
    const visibleDots = 5;
    
    if (totalDots <= visibleDots) {
      return 0; // No scrolling needed if 5 or fewer dots
    }
    
    // Calculate which group of 5 dots to show
    const groupIndex = Math.floor(currentSlide / visibleDots);
    const scrollPosition = groupIndex * visibleDots;
    
    // Handle edge cases - when near the end, show the last group
    if (currentSlide >= totalDots - visibleDots) {
      return (totalDots - visibleDots) * (0.5 + 0.25);
    }
    
    // Transform calculation: scrollPosition * (dotWidth + gap)
    return scrollPosition * (0.5 + 0.25); // 0.5rem dot + 0.25rem gap
  };

  const goToHomeFromLogo = useCallback(() => {
    setCurrentPage('home');
    setActiveSection('home');
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    document.querySelector('.mobile-menu')?.classList.remove('active');
  }, [setCurrentPage, setActiveSection]);

  return (
    <div className="services-container services-page--light">
      {/* Navigation Bar */}
      <div className="icon-navbar visible">
        <div className="button-container">
          {/* GGC Logo */}
          <button
            type="button"
            className="nav-logo"
            onClick={goToHomeFromLogo}
            aria-label="Go to home"
            title="Home"
          >
            <img 
              src={`${process.env.PUBLIC_URL}/geologo.png`} 
              alt="" 
              className="nav-logo-img"
            />
          </button>
          
          <button 
            className="button"
            onClick={() => {
              setCurrentPage('home');
              setActiveSection('home');
            }}
            title="Home"
          >
            Home
          </button>
          
          <button 
            className="button active"
            onClick={() => {
              setCurrentPage('services');
              setActiveSection('services');
            }}
            title="Services"
          >
            Services
          </button>

          <button 
            className="button"
            onClick={() => {
              setCurrentPage('home');
              setTimeout(() => {
                setActiveSection('projects');
                const projectsSection = document.getElementById('projects');
                if (projectsSection) {
                  projectsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }, 120);
            }}
            title="Projects"
          >
            Projects
          </button>

          <button 
            className="button"
            onClick={(e) => {
              e.preventDefault();
              // Go to home page first
              setCurrentPage('home');
              setActiveSection('branches');
              
              // Use a more reliable method to wait for the element
              const waitForBranchesSection = () => {
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  // If not found, try again in 50ms
                  setTimeout(waitForBranchesSection, 50);
                }
              };
              
              // Start waiting for the section after a short delay
              setTimeout(waitForBranchesSection, 150);
            }}
            title="Branches"
          >
            Branches
          </button>

          <button 
            className="button"
            onClick={(e) => {
              e.preventDefault();
              // Go to testing page first
              setCurrentPage('testing');
              setActiveSection('testing');
              
              // Use a more reliable method to wait for the element
              const waitForTestingSection = () => {
                const testingSection = document.getElementById('testing');
                if (testingSection) {
                  testingSection.scrollIntoView({ behavior: 'smooth' });
                } else {
                  // If not found, try again in 50ms
                  setTimeout(waitForTestingSection, 50);
                }
              };
              
              // Start waiting for the section after a short delay
              setTimeout(waitForTestingSection, 150);
            }}
            title="Testing"
          >
            Testing
          </button>

          <button
            className="button"
            onClick={() => {
              setCurrentPage('about');
              setActiveSection('about');
              window.scrollTo(0, 0);
            }}
            title="About"
          >
            About
          </button>
        </div>

        {/* Mobile Top Navigation */}
        <div className="mobile-nav-container">
          <button
            type="button"
            className="mobile-logo"
            onClick={goToHomeFromLogo}
            aria-label="Go to home"
            title="Home"
          >
            GEOMATIXS
          </button>
          <button 
            className="mobile-menu-toggle"
            onClick={() => {
              const menu = document.querySelector('.mobile-menu');
              if (menu) {
                menu.classList.toggle('active');
              }
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z"/>
            </svg>
          </button>
        </div>

        {/* Mobile Dropdown Menu */}
        <div className="mobile-menu">
          <button 
            className="mobile-menu-item"
            onClick={() => {
              setCurrentPage('home');
              setActiveSection('home');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            <span>Home</span>
          </button>
          <button 
            className="mobile-menu-item active"
            onClick={() => {
              setCurrentPage('services');
              setActiveSection('services');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
            <span>Services</span>
          </button>
          <button 
            className="mobile-menu-item"
            onClick={() => {
              setCurrentPage('home');
              setTimeout(() => {
                setActiveSection('projects');
                const projectsSection = document.getElementById('projects');
                if (projectsSection) {
                  projectsSection.scrollIntoView({ behavior: 'smooth' });
                }
              }, 120);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
            </svg>
            <span>Projects</span>
          </button>
          <button 
            className="mobile-menu-item"
            onClick={(e) => {
              e.preventDefault();
              if (setCurrentPage !== 'home') {
                setCurrentPage('home');
                setTimeout(() => {
                  setActiveSection('branches');
                  const branchesSection = document.getElementById('branches');
                  if (branchesSection) {
                    branchesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              } else {
                setActiveSection('branches');
                const branchesSection = document.getElementById('branches');
                if (branchesSection) {
                  branchesSection.scrollIntoView({ behavior: 'smooth' });
                }
              }
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            <span>Branches</span>
          </button>
          <button 
            className="mobile-menu-item"
            onClick={(e) => {
              e.preventDefault();
              setCurrentPage('testing');
              setActiveSection('testing');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 11H7v6h2v-6zm4 0h-2v6h2v-6zm4 0h-2v6h2v-6zm2.5-9H19V1h-2v1H7V1H5v1H4.5C3.67 2 3 2.67 3 3.5v15C3 19.33 3.67 20 4.5 20h15c.83 0 1.5-.67 1.5-1.5v-15C21 2.67 20.33 2 19.5 2z"/>
            </svg>
            <span>Testing</span>
          </button>
          <button
            className="mobile-menu-item"
            onClick={() => {
              setCurrentPage('about');
              setActiveSection('about');
              window.scrollTo(0, 0);
              document.querySelector('.mobile-menu')?.classList.remove('active');
            }}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <span>About</span>
          </button>
        </div>
      </div>

      <div className="services-nav">
        <div className="services-macro-row" role="tablist" aria-label="Service category">
          {Object.entries(SERVICE_MACRO_GROUPS).map(([key, g]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={serviceMacroGroup === key}
              className={`services-macro-button ${serviceMacroGroup === key ? 'active' : ''}`}
              onClick={() => {
                const next = g.categories;
                setServiceMacroGroup(key);
                const nextTab = next.includes(activeTab) ? activeTab : next[0];
                beginTabTransition(nextTab);
              }}
              title={g.label}
            >
              {g.shortLabel}
            </button>
          ))}
        </div>
        <div className="nav-scroll-container">
          {visibleCategories.map((category) => (
            <button 
              key={category}
              className={`nav-button ${activeTab === category ? 'active' : ''}`}
              onClick={() => {
                beginTabTransition(category);
              }}
              title={category}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      <div
        className={`service-content-container ${
          contentAnimPhase === 'exit' ? 'service-content-exit' : contentAnimPhase === 'enter' ? 'service-content-enter' : ''
        }`}
      >
        <div className="service-workspace">
          <div className="service-intro">
            <div className="service-intro__text">
              <header className="service-header">
                <p className="service-header-eyebrow">Capabilities</p>
                <h1 id="service-main-heading" className="service-title">{currentService.title}</h1>
                <p className="service-description">{currentService.description}</p>
                <p className="service-nav-hint">
                  Choose a discipline above. Every test is shown on this page with photos and full methodology. Use <strong>Inquire</strong> to reach us about a specific scope.
                </p>
              </header>
              <div className="service-intro__cta">
                <button type="button" className="service-inquire-btn" onClick={goToInquire}>
                  Inquire about {currentService.title}
                </button>
              </div>
            </div>
            <div className="service-intro__media service-intro__media--carousel" aria-label="Service photography">
              <div className="service-image service-image--intro">
                {carouselImages.length > 0 ? (
                <>
                <div className="image-container">
                  {loadingImages.has(carouselImages[currentSlide]) ? (
                    <div className="image-loading">
                      <div className="loading-spinner"></div>
                      <p>Loading image...</p>
                    </div>
                  ) : (
                    <img 
                      src={carouselImages[currentSlide]} 
                      decoding="async"
                      loading="eager"
                      fetchpriority="high"
                      alt={`${currentService.title} — ${currentSlide + 1} of ${carouselImages.length}`}
                      className={`carousel-image ${loadedImages.has(carouselImages[currentSlide]) ? 'loaded' : 'loading'}`}
                      onError={onTestImageError}
                    />
                  )}
                </div>
                <div className="carousel-controls">
                  <button 
                    onClick={prevSlide} 
                    className="carousel-button"
                    type="button"
                    aria-label="Previous photo"
                    disabled={loadingImages.has(carouselImages[currentSlide])}
                  >
                    ←
                  </button>
                  <div className="carousel-dots-container">
                    <div 
                      className="carousel-dots"
                      style={{
                        transform: `translateX(-${getDotsTransform()}rem)`
                      }}
                    >
                      {carouselImages.map((_, idx) => (
                        <button
                          key={idx}
                          type="button"
                          aria-label={`Show photo ${idx + 1}`}
                          className={`carousel-dot ${idx === currentSlide ? 'active' : ''} ${loadedImages.has(carouselImages[idx]) ? 'loaded' : 'loading'}`}
                          onClick={() => setCurrentSlide(idx)}
                          disabled={loadingImages.has(carouselImages[idx])}
                        />
                      ))}
                    </div>
                  </div>
                  <button 
                    onClick={nextSlide} 
                    className="carousel-button"
                    type="button"
                    aria-label="Next photo"
                    disabled={loadingImages.has(carouselImages[currentSlide])}
                  >
                    →
                  </button>
                </div>
                </>
                ) : (
                  <div className="image-container image-container--empty" role="status">
                    <img
                      src={TEST_IMAGE_PLACEHOLDER}
                      alt=""
                      className="carousel-image carousel-image--placeholder loaded"
                      decoding="async"
                    />
                    <p className="service-carousel-empty">Photos for this service will appear here.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="service-tests" role="region" aria-labelledby="service-tests-heading">
            <div className="service-tests__head">
              <h2 id="service-tests-heading" className="service-tests__title">
                Tests &amp; methods
              </h2>
              <p className="service-tests__sub">
                {serviceHasMethodDrawers
                  ? 'Click a test card (photo or description) to enlarge its image — carousel photos above stay slideshow-only. Sub-methods with extra methodology expand from + / −. Use Inquire for quotes.'
                  : 'Click a test card to enlarge its image. The carousel above stays slideshow-only (use arrows or dots). Use Inquire for quotes.'}
              </p>
            </div>
            <div
              ref={testsListRef}
              className="service-tests-list"
              aria-labelledby="service-main-heading"
            >
              {mainTestingGroups.map((group, groupIndex) => {
                const headerText = getItemDisplayText(group.header);
                const groupDescription = groupDescriptionByHeader?.[String(group.header).trim()] || '';
                const imgs = carouselImages || [];
                const groupImagesMap = currentService.groupImages;
                const resolvedSrc =
                  groupImagesMap?.[headerText] ||
                  (imgs.length ? imgs[groupIndex % imgs.length] : '');
                const imgSrc = resolvedSrc || TEST_IMAGE_PLACEHOLDER;
                const listHasDrawerItems =
                  group.children.length > 0 &&
                  group.children.some((ch) => Boolean(itemDescriptions?.[getItemDisplayText(ch)]));

                return (
                  <article
                    key={`${headerText}-${groupIndex}`}
                    className="service-group-card reveal-on-scroll"
                    data-reveal
                  >
                    <div
                      className="service-group-card__grid service-group-card__grid--clickable"
                      role="presentation"
                      onClick={(e) => {
                        if (e.target.closest('button, a, input, textarea, select, label')) return;
                        setServiceTestPhotoLightbox({
                          title: headerText,
                          src: imgSrc,
                          alt: resolvedSrc
                            ? `${headerText} — representative laboratory or field work`
                            : `${headerText} — image placeholder`
                        });
                      }}
                    >
                      <figure className="service-group-card__figure">
                        <img
                          src={imgSrc}
                          alt={
                            resolvedSrc
                              ? `${headerText} — representative laboratory or field work`
                              : `${headerText} — image placeholder`
                          }
                          className={`service-group-card__img${resolvedSrc ? '' : ' service-group-card__img--placeholder'}`}
                          loading="lazy"
                          decoding="async"
                          onError={onTestImageError}
                        />
                      </figure>
                      <div className="service-group-card__content">
                        <div className="service-group-card__headrow">
                          <h3 className="service-group-card__title">{headerText}</h3>
                          <button type="button" className="service-inquire-btn" onClick={goToInquire}>
                            Inquire
                          </button>
                        </div>
                        {groupDescription ? (
                          <p className="service-group-card__prose">{groupDescription}</p>
                        ) : null}

                        {group.children.length > 0 ? (
                          <ul
                            className={`service-sub-list ${listHasDrawerItems ? 'service-sub-list--method-drawer' : ''}`}
                          >
                            {group.children.map((item, index) => {
                              const itemText = getItemDisplayText(item);
                              const itemDesc = itemDescriptions?.[itemText];
                              const hasExtendedDef = Boolean(itemDesc);
                              const subClass = getItemClassName(item);
                              const drawerKey = `${displayedTab}-${groupIndex}-${index}-${itemText}`;
                              const drawerOpen = methodDrawerKeys.has(drawerKey);
                              const panelId = `method-drawer-${displayedTab}-${groupIndex}-${index}`;

                              if (hasExtendedDef) {
                                return (
                                  <li
                                    key={`${groupIndex}-${index}-${itemText}`}
                                    className={`service-sub-card service-sub-card--drawer ${subClass}`}
                                  >
                                    <button
                                      type="button"
                                      className="service-drawer-trigger"
                                      onClick={() => toggleMethodDrawer(drawerKey)}
                                      aria-expanded={drawerOpen}
                                      aria-controls={panelId}
                                    >
                                      <span className="service-drawer-trigger__title">{itemText}</span>
                                      <span className="service-drawer-trigger__chev" aria-hidden="true">
                                        {drawerOpen ? '−' : '+'}
                                      </span>
                                    </button>
                                    <div
                                      id={panelId}
                                      className={`service-drawer-panel ${drawerOpen ? 'is-open' : ''}`}
                                      role="region"
                                      aria-hidden={!drawerOpen}
                                    >
                                      <div className="service-drawer-panel__inner">
                                        <p className="service-sub-card__prose service-drawer-panel__prose">{itemDesc}</p>
                                        <button
                                          type="button"
                                          className="service-inquire-btn service-inquire-btn--ghost service-drawer-panel__cta"
                                          onClick={goToInquire}
                                        >
                                          Inquire
                                        </button>
                                      </div>
                                    </div>
                                  </li>
                                );
                              }

                              return (
                                <li
                                  key={`${groupIndex}-${index}-${itemText}`}
                                  className={`service-sub-card reveal-on-scroll ${subClass}`}
                                  data-reveal
                                >
                                  <div className="service-sub-card__row">
                                    <h4 className="service-sub-card__name">{itemText}</h4>
                                    <button type="button" className="service-inquire-btn service-inquire-btn--ghost" onClick={goToInquire}>
                                      Inquire
                                    </button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {serviceTestPhotoLightbox && (
        <div className="service-test-zoom" role="dialog" aria-modal="true" aria-labelledby="service-test-zoom-heading">
          <button
            type="button"
            className="service-test-zoom__backdrop"
            onClick={() => setServiceTestPhotoLightbox(null)}
            aria-label="Close enlarged photo"
          />
          <div className="service-test-zoom__card">
            <div className="service-test-zoom__top">
              <h2 id="service-test-zoom-heading" className="service-test-zoom__title">
                {serviceTestPhotoLightbox.title}
              </h2>
              <button
                type="button"
                className="service-test-zoom__close"
                onClick={() => setServiceTestPhotoLightbox(null)}
                aria-label="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="service-test-zoom__picture">
              <img
                src={serviceTestPhotoLightbox.src}
                alt={serviceTestPhotoLightbox.alt}
                className="service-test-zoom__img"
                onError={onTestImageError}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;